<template>
  <div>
    <v-card>
      <v-card-title>
        Courses
        <v-spacer></v-spacer>
        <v-text-field
          v-model="search"
          append-icon="mdi-magnify"
          label="Search"
          single-line
          hide-details
          @input="debouncedSearch"
          @keydown.enter="manualSearch"
          clearable
        ></v-text-field>
      </v-card-title>

      <!-- Filters as separate buttons above the table -->
      <v-card-text>
        <v-row>
          <v-col cols="12" sm="6" md="3" lg="2">
            <v-autocomplete
              v-model="filters.term"
              :items="termOptions"
              label="Term"
              clearable
              @change="applyFilters"
              outlined
              dense
              hide-details
              auto-select-first
              :search-input.sync="termSearch"
            ></v-autocomplete>
          </v-col>
          <v-col cols="12" sm="6" md="3" lg="2">
            <v-autocomplete
              v-model="filters.subject_code"
              :items="subjectOptions"
              label="Subject"
              clearable
              @change="applyFilters"
              outlined
              dense
              hide-details
              auto-select-first
              :search-input.sync="subjectSearch"
            ></v-autocomplete>
          </v-col>
          <v-col cols="12" sm="6" md="3" lg="3">
            <v-autocomplete
              v-model="filters.section_session_description"
              :items="sessionDescriptionOptions"
              label="Session Description"
              clearable
              @change="applyFilters"
              outlined
              dense
              hide-details
              auto-select-first
              :search-input.sync="sessionDescriptionSearch"
            ></v-autocomplete>
          </v-col>
          <v-col cols="12" sm="6" md="3" lg="2">
            <v-autocomplete
              v-model="filters.cross_list_group_id"
              :items="crossListGroupOptions"
              label="Cross List Group"
              clearable
              @change="applyFilters"
              outlined
              dense
              hide-details
              auto-select-first
              :search-input.sync="crossListGroupSearch"
            ></v-autocomplete>
          </v-col>
          <v-col cols="12" sm="6" md="3" lg="3">
            <v-autocomplete
              v-model="filters.instructor"
              :items="instructorOptions"
              label="Instructor"
              clearable
              @change="applyFilters"
              outlined
              dense
              hide-details
              auto-select-first
              :search-input.sync="instructorSearch"
            ></v-autocomplete>
          </v-col>
        </v-row>
        <v-row v-if="hasActiveFilters">
          <v-col>
            <v-chip
              color="primary"
              close
              @click:close="clearFilters"
            >
              Clear All Filters
            </v-chip>
          </v-col>
        </v-row>
      </v-card-text>

      <v-data-table
        :headers="headers"
        :items="courses"
        :options.sync="options"
        :server-items-length="totalCourses"
        :loading="loading"
        class="elevation-1"
        @update:options="fetchCourses"
        :footer-props="{
          'items-per-page-options': [10, 25, 50, 100, -1],
          'items-per-page-text': 'Rows per page:',
          'items-per-page-all-text': 'All'
        }"
      >
        <!-- Add a template for the instructors column -->
        <template v-slot:item.instructors="{ item }">
          <div v-if="item.instructors && item.instructors.length">
            <div v-for="(instructor, i) in item.instructors" :key="i">
              {{ instructor.firstName }} {{ instructor.lastName }}
              <v-chip x-small v-if="instructor.primaryInd === 'Y'" color="primary">Primary</v-chip>
            </div>
          </div>
          <div v-else>No instructors</div>
        </template>

        <!-- Add a template for the cross_list_group_id column -->
        <template v-slot:item.cross_list_group_id="{ item }">
          <v-chip
            v-if="item.cross_list_group_id"
            small
            color="accent"
            text-color="white"
          >
            {{ item.cross_list_group_id }}
          </v-chip>
          <span v-else>.</span>
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
import _ from 'lodash';
import axios from 'axios';

export default {
  data() {
    return {
      courses: [],
      totalCourses: 0,
      loading: true,
      app_name: process.env.MIX_APP_NAME,
      search: '',
      options: {
        page: 1,
        itemsPerPage: 10,
        sortBy: ['term'],
        sortDesc: [false]
      },
      headers: [
        { text: 'Term', value: 'term', sortable: true },
        { text: 'CRN', value: 'crn', sortable: true },
        { text: 'Subject', value: 'subject_code', sortable: true },
        { text: 'Number', value: 'number', sortable: true },
        { text: 'Title', value: 'title', sortable: true },
        { text: 'Section', value: 'section_number', sortable: true },
        { text: 'Status', value: 'section_status_description', sortable: true },
        { text: 'Cross List Group', value: 'cross_list_group_id', sortable: true },
        { text: 'Schedule Description', value: 'schedule_type_description', sortable: true },
        { text: 'Instructors', value: 'instructors', sortable: false, filter: value => true },
        { text: 'Session Description', value: 'section_session_description', sortable: true }
      ],
      filters: {
        term: null,
        subject_code: null,
        section_session_description: null,
        cross_list_group_id: null,
        instructor: null
      },
      // Search inputs for autocomplete
      termSearch: null,
      subjectSearch: null,
      sessionDescriptionSearch: null,
      crossListGroupSearch: null,
      instructorSearch: null,
      // Options for filter selects
      termOptions: [],
      subjectOptions: [],
      sessionDescriptionOptions: [],
      crossListGroupOptions: [],
      instructorOptions: []
    };
  },
  computed: {
    hasActiveFilters() {
      return this.filters.term || this.filters.subject_code || 
             this.filters.section_session_description || this.filters.cross_list_group_id ||
             this.filters.instructor;
    }
  },
  created() {
    this.debouncedSearch = _.debounce(() => {
      // Reset to page 1 when search changes
      this.options.page = 1;
      this.fetchCourses();
    }, 500);
    
    // Initialize with some data first
    this.initializeFilterOptions();
    // Then fetch real data
    this.fetchCourses();
    this.fetchFilterOptions();
  },
  watch: {
    // Add watchers for the search inputs
    termSearch(val) {
      // You can add additional logic here to filter the options as the user types
      if (val && val !== this.filters.term) {
        // Additional filtering logic can go here
      }
    },
    subjectSearch(val) {
      if (val && val !== this.filters.subject_code) {
        // Additional filtering logic can go here
      }
    },
    sessionDescriptionSearch(val) {
      if (val && val !== this.filters.section_session_description) {
        // Additional filtering logic can go here
      }
    },
    crossListGroupSearch(val) {
      if (val && val !== this.filters.cross_list_group_id) {
        // Additional filtering logic can go here
      }
    },
    instructorSearch(val) {
      if (val && val !== this.filters.instructor) {
        // Additional filtering logic can go here
      }
    }
  },
  methods: {
    fetchCourses() {
      this.loading = true;
      
      const { page, itemsPerPage, sortBy, sortDesc } = this.options;
      
      let params = {
        page: page || 1,
        itemsPerPage: itemsPerPage || 10,
        sortBy: sortBy && sortBy.length ? sortBy[0] : 'id',
        sortDesc: sortDesc && sortDesc.length ? sortDesc[0] : false,
        search: this.search,
        ...this.filters
      };
      
      // Show a warning if selecting "All" and there might be many records
      if (params.itemsPerPage === -1 && this.totalCourses > 1000) {
        if (!confirm(`Loading all ${this.totalCourses} records may slow down your browser. Continue?`)) {
          // If user cancels, reset to a reasonable page size
          this.options.itemsPerPage = 100;
          params.itemsPerPage = 100;
        }
      }
      
      axios.get('/' + this.app_name + '/api/courses', { params })
        .then(response => {
          this.courses = response.data.data;
          this.totalCourses = response.data.total;
          this.loading = false;
        })
        .catch(error => {
          console.error('Error fetching courses:', error);
          this.loading = false;
          // Display error message to user
          alert('An error occurred while fetching data. Please try again.');
        });
    },
    
    manualSearch() {
      // Reset to page 1 and fetch courses immediately
      this.options.page = 1;
      this.fetchCourses();
    },
    applyFilters() {
      // Reset to page 1 when filters change
      this.options.page = 1;
      this.fetchCourses();
    },
    clearFilters() {
      this.filters = {
        term: null,
        subject_code: null,
        section_session_description: null,
        cross_list_group_id: null,
        instructor: null
      };
      this.applyFilters();
    },
    initializeFilterOptions() {
      // Set some initial options to ensure the dropdowns work
      this.termOptions = ['420255', '420258']; // Sample term values
      this.subjectOptions = [
        { text: 'ART - Visual Arts', value: 'ART' },
        { text: 'CSC - Computer Science', value: 'CSC' }
      ];
      this.sessionDescriptionOptions = [
        { text: 'Online', value: 'Online' },
        { text: 'On Campus', value: 'On Campus' }
      ];
      this.crossListGroupOptions = [
        { text: 'AR04', value: 'AR04' },
        { text: 'CS01', value: 'CS01' }
      ];
      this.instructorOptions = [
        { text: 'No instructors', value: 'No instructors' },
        { text: 'Brytton Bjorngaard', value: 'Brytton Bjorngaard' },
        { text: 'John Smith', value: 'John Smith' }
      ];
    },
    fetchFilterOptions() {
      // Fetch unique values for filter dropdowns
      axios.get('/' + this.app_name + '/api/courses/filter-options')
        .then(response => {
          if (response.data.terms && response.data.terms.length > 0) {
            this.termOptions = response.data.terms;
          }
          
          if (response.data.subjects && response.data.subjects.length > 0) {
            this.subjectOptions = response.data.subjects;
          }
          
          if (response.data.session_descriptions && response.data.session_descriptions.length > 0) {
            this.sessionDescriptionOptions = response.data.session_descriptions;
          }
          
          if (response.data.cross_list_groups && response.data.cross_list_groups.length > 0) {
            this.crossListGroupOptions = response.data.cross_list_groups;
          }
          
          if (response.data.instructors && response.data.instructors.length > 0) {
            this.instructorOptions = response.data.instructors;
          }
        })
        .catch(error => {
          console.error('Error fetching filter options:', error);
          // Continue with default values
        });
    }
  }
};
</script>
